import React, { useState, useRef, useEffect, useCallback } from 'react';
import { IconGlobe, IconChevronDown } from '@amzn/stencil-react-icons';
import { ORDERED_SUPPORTED_LANGUAGES } from '@amzn/atoz-supported-languages';
import { persistLocale, applyDocumentDirection, detectBrowserLocale } from '../utils/localeDetection';
import { LANGUAGE_RESET_TIMEOUT } from '../constants';
import { metricsHelper, MetricNames, DimensionNames } from '../metrics';

interface LanguageSelectorDropdownProps {
    currentLocale: string;
    onChange: (locale: string) => void;
    onReset?: () => void;
}

const LanguageSelectorDropdown: React.FC<LanguageSelectorDropdownProps> = ({
    currentLocale,
    onChange,
    onReset,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
    // Get browser locale once on mount - this is the device's default language
    const browserLocaleRef = useRef<string>(detectBrowserLocale());

    const currentLanguage = ORDERED_SUPPORTED_LANGUAGES.find(
        (lang) => lang.locale === currentLocale
    );
    const currentLanguageName = currentLanguage?.name || 'English (US)';

    const resetToDefault = useCallback(() => {
        if (currentLocale !== browserLocaleRef.current && onReset) {
            // Publish auto-reset metric with site_code dimension
            metricsHelper.publishCounter(MetricNames.LANGUAGE_AUTO_RESET, {
                [DimensionNames.PREVIOUS_LOCALE]: currentLocale,
            });

            onReset();
        }
    }, [currentLocale, onReset]);

    const startInactivityTimer = useCallback(() => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
        }

        // Only start timer if user changed from device's browser language
        if (currentLocale !== browserLocaleRef.current) {
            inactivityTimerRef.current = setTimeout(() => {
                resetToDefault();
            }, LANGUAGE_RESET_TIMEOUT * 1000);
        }
    }, [currentLocale, resetToDefault]);

    useEffect(() => {
        startInactivityTimer();
        return () => {
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
            }
        };
    }, [currentLocale, startInactivityTimer]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsOpen(false);
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    const handleSelect = (locale: string) => {
        if (locale === currentLocale) {
            setIsOpen(false);
            return;
        }

        // Publish language change metric with site_code dimension
        metricsHelper.publishCounter(MetricNames.LANGUAGE_CHANGE, {
            [DimensionNames.LOCALE]: locale,
        });

        persistLocale(locale);
        applyDocumentDirection(locale);
        onChange(locale);
        setIsOpen(false);
    };

    const isRTL = document.documentElement.dir === 'rtl';

    return (
        <div
            ref={dropdownRef}
            style={{
                position: 'relative',
                marginInlineEnd: '24px',
            }}
            data-testid="language-selector-container"
        >
            <button
                onClick={() => setIsOpen(!isOpen)}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label="Select language"
                style={{
                    display: 'flex',
                    flexDirection: isRTL ? 'row-reverse' : 'row',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#1f2937',
                    transition: 'all 0.2s ease',
                    minWidth: '180px',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                    e.currentTarget.style.borderColor = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = '#d1d5db';
                }}
            >
                <IconGlobe size={18} aria-hidden />
                <span style={{ flex: 1, textAlign: isRTL ? 'right' : 'left' }}>{currentLanguageName}</span>
                <IconChevronDown
                    size={16}
                    aria-hidden
                    style={{
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                    }}
                />
            </button>

            {isOpen && (
                <div
                    role="listbox"
                    aria-label="Available languages"
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                        maxHeight: '320px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        minWidth: '220px',
                        padding: '6px',
                    }}
                >
                    {ORDERED_SUPPORTED_LANGUAGES.map((language) => (
                        <div
                            key={language.locale}
                            role="option"
                            aria-selected={language.locale === currentLocale}
                            onClick={() => handleSelect(language.locale)}
                            style={{
                                padding: '10px 14px',
                                cursor: 'pointer',
                                borderRadius: '8px',
                                fontSize: '14px',
                                color: language.locale === currentLocale ? '#2563eb' : '#374151',
                                backgroundColor: language.locale === currentLocale ? '#eff6ff' : 'transparent',
                                fontWeight: language.locale === currentLocale ? 600 : 400,
                                transition: 'all 0.15s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                            onMouseEnter={(e) => {
                                if (language.locale !== currentLocale) {
                                    e.currentTarget.style.backgroundColor = '#f9fafb';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (language.locale !== currentLocale) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }
                            }}
                        >
                            <span>{language.name}</span>
                            {language.locale === currentLocale && (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default LanguageSelectorDropdown;
